import React, {useState} from 'react'
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material'
import {
  regularOptions,
  irregularOptions,
  strongOptions,
  weakOptions,
  chestPainOptions,
  nailBedsOptions,
  edemaOptions,
  capillaryRefillOptions,
  otherCardiovascularOptions,
  clearOptions,
  equalbilaterallyOptions,
  courseOptions,
  ralesOptions,
  rhonchiOptions,
  wheezeOptions,
  pleuralOptions,
  coughOptions,
  yesNoOptionsForm3 as yesNoOptions,
  initialStateForm3 as initialState,
} from './NURSES_ASSESSMENT_FORM_CONSTANTS'

function CheckboxGroup({label, options, value, onChange}) {
  return (
    <FormControl component='fieldset' sx={{mb: 2}}>
      <Typography variant='subtitle2' color='primary'>
        {label}
      </Typography>
      <FormGroup row>
        {options.map((opt) => (
          <FormControlLabel
            key={opt.value}
            control={
              <Checkbox
                checked={value.includes(opt.value)}
                onChange={(e) => onChange(opt.value, e.target.checked)}
              />
            }
            label={opt.label}
            sx={{mr: 1, width: 300}}
          />
        ))}
      </FormGroup>
    </FormControl>
  )
}

const NursesAssessmentForm3 = ({data = {}, setFormValues, onSubmit, buttons}) => {
  const [formState, setFormState] = useState({...initialState, ...data})

  const handleChange = (name, value) => {
    setFormState((prev) => {
      const updated = {...prev, [name]: value}
      setFormValues && setFormValues(updated)
      return updated
    })
  }

  const handleCheckboxChange = (name, optionValue, checked) => {
    setFormState((prev) => {
      const arr = Array.isArray(prev[name]) ? [...prev[name]] : []
      if (checked) {
        if (!arr.includes(optionValue)) arr.push(optionValue)
      } else {
        const idx = arr.indexOf(optionValue)
        if (idx > -1) arr.splice(idx, 1)
      }
      const updated = {...prev, [name]: arr}
      setFormValues && setFormValues(updated)
      return updated
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit && onSubmit(formState)
  }

  return (
    <Box component='form' onSubmit={handleSubmit} sx={{mt: 2, mx: 'auto'}}>
      <Box>
        <Typography variant='h6' color='primary' sx={{fontSize: '16px', fontWeight: 'bold', mb: 2}}>
          Cardiovascular
        </Typography>
        {/* Cardiovascular Section */}
        <CheckboxGroup
          label='Regular Pulses'
          options={regularOptions}
          value={formState.regular}
          onChange={(val, checked) => handleCheckboxChange('regular', val, checked)}
        />
        <br></br>
        <CheckboxGroup
          label='Irregular Pulses'
          options={irregularOptions}
          value={formState.irregular}
          onChange={(val, checked) => handleCheckboxChange('irregular', val, checked)}
        />
        <br></br>
        <CheckboxGroup
          label='Strong Pulses'
          options={strongOptions}
          value={formState.strong}
          onChange={(val, checked) => handleCheckboxChange('strong', val, checked)}
        />
        <br></br>
        <CheckboxGroup
          label='Weak Pulses'
          options={weakOptions}
          value={formState.weak}
          onChange={(val, checked) => handleCheckboxChange('weak', val, checked)}
        />
        {/* Grouped select fields: chestPain, nailBeds */}
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2}}>
          <FormControl fullWidth size="small" sx={{flex: '1 1 30%', minWidth: 180}}>
            <InputLabel>Chest Pain</InputLabel>
            <Select
              size="small"
              value={formState.chestPain}
              label='Chest Pain'
              onChange={(e) => handleChange('chestPain', e.target.value)}
            >
              {chestPainOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{flex: '1 1 30%', minWidth: 180}}>
            <InputLabel>Nail Beds</InputLabel>
            <Select
              size="small"
              value={formState.nailBeds}
              label='Nail Beds'
              onChange={(e) => handleChange('nailBeds', e.target.value)}
            >
              {nailBedsOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <TextField
          label='Pain Description'
          value={formState.chestPainLoc}
          onChange={(e) => handleChange('chestPainLoc', e.target.value)}
          fullWidth
          sx={{mb: 2}}
        />
        <CheckboxGroup
          label='Edema (4+)'
          options={edemaOptions}
          value={formState.edemafour}
          onChange={(val, checked) => handleCheckboxChange('edemafour', val, checked)}
        />
        <TextField
          label='Edema (4+) Details'
          value={formState.edemafourDetails}
          onChange={(e) => handleChange('edemafourDetails', e.target.value)}
          fullWidth
          sx={{mb: 2}}
        />
        <CheckboxGroup
          label='Edema (3+)'
          options={edemaOptions}
          value={formState.edemathree}
          onChange={(val, checked) => handleCheckboxChange('edemathree', val, checked)}
        />
        <TextField
          label='Edema (3+) Details'
          value={formState.edemathreeDetails}
          onChange={(e) => handleChange('edemathreeDetails', e.target.value)}
          fullWidth
          sx={{mb: 2}}
        />
        <CheckboxGroup
          label='Edema (2+)'
          options={edemaOptions}
          value={formState.edematwo}
          onChange={(val, checked) => handleCheckboxChange('edematwo', val, checked)}
        />
        <TextField
          label='Edema (2+) Details'
          value={formState.edematwoDetails}
          onChange={(e) => handleChange('edematwoDetails', e.target.value)}
          fullWidth
          sx={{mb: 2}}
        />
        <CheckboxGroup
          label='Edema (1+)'
          options={edemaOptions}
          value={formState.edemaone}
          onChange={(val, checked) => handleCheckboxChange('edemaone', val, checked)}
        />
        <TextField
          label='Edema (1+) Details'
          value={formState.edemaoneDetails}
          onChange={(e) => handleChange('edemaoneDetails', e.target.value)}
          fullWidth
          sx={{mb: 2}}
        />
        <CheckboxGroup
          label='Capillary Refill'
          options={capillaryRefillOptions}
          value={formState.capillaryRefill}
          onChange={(val, checked) => handleCheckboxChange('capillaryRefill', val, checked)}
        />
        <TextField
          label='Capillary Refill Other Details'
          value={formState.capillaryRefillOtherDetails}
          onChange={(e) => handleChange('capillaryRefillOtherDetails', e.target.value)}
          fullWidth
          sx={{mb: 2}}
        />
        <TextField
          label='Capillary Refill Time'
          value={formState.capillaryRefillTime}
          onChange={(e) => handleChange('capillaryRefillTime', e.target.value)}
          fullWidth
          sx={{mb: 2}}
        />
        <CheckboxGroup
          label='Other Cardiovascular'
          options={otherCardiovascularOptions}
          value={formState.otherCardiovascular}
          onChange={(val, checked) => handleCheckboxChange('otherCardiovascular', val, checked)}
        />
        <TextField
          label='Cardian Rhythm Details'
          value={formState.cardianRhythmDetails}
          onChange={(e) => handleChange('cardianRhythmDetails', e.target.value)}
          fullWidth
          sx={{mb: 2}}
        />

        {/* Pulmonary Section */}
        <Typography
          variant='subtitle1'
          sx={{mt: 3, mb: 1, fontSize: '16px', fontWeight: 'bold'}}
          color='primary'
        >
          Pulmonary
        </Typography>
        <CheckboxGroup
          label='Clear'
          options={clearOptions}
          value={formState.clear}
          onChange={(val, checked) => handleCheckboxChange('clear', val, checked)}
        />
        <br></br>
        <CheckboxGroup
          label='Equal Bilaterally'
          options={equalbilaterallyOptions}
          value={formState.equalbilaterally}
          onChange={(val, checked) => handleCheckboxChange('equalbilaterally', val, checked)}
        />
        <br></br>
        <CheckboxGroup
          label='Course'
          options={courseOptions}
          value={formState.course}
          onChange={(val, checked) => handleCheckboxChange('course', val, checked)}
        />
        <br></br>
        <CheckboxGroup
          label='Rales'
          options={ralesOptions}
          value={formState.rales}
          onChange={(val, checked) => handleCheckboxChange('rales', val, checked)}
        />
        <br></br>
        <CheckboxGroup
          label='Rhonchi'
          options={rhonchiOptions}
          value={formState.rhonchi}
          onChange={(val, checked) => handleCheckboxChange('rhonchi', val, checked)}
        />
        <br></br>
        <CheckboxGroup
          label='Wheeze'
          options={wheezeOptions}
          value={formState.wheeze}
          onChange={(val, checked) => handleCheckboxChange('wheeze', val, checked)}
        />
        <br></br>
        <CheckboxGroup
          label='Pleural'
          options={pleuralOptions}
          value={formState.pleural}
          onChange={(val, checked) => handleCheckboxChange('pleural', val, checked)}
        />
        <TextField
          label='Other Sound Details'
          value={formState.otherSoundDetails}
          onChange={(e) => handleChange('otherSoundDetails', e.target.value)}
          fullWidth
          sx={{mb: 2}}
        />
        <FormControl fullWidth size="small" sx={{mb: 2}}>
          <InputLabel>Cough</InputLabel>
          <Select
            value={formState.cough}
            label='Cough'
            onChange={(e) => handleChange('cough', e.target.value)}
          >
            {coughOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label='Cough Color'
            value={formState.coughColor}
            onChange={(e) => handleChange('coughColor', e.target.value)}
            fullWidth
          />
          <TextField
            label='Cough Quality'
            value={formState.coughQuality}
            onChange={(e) => handleChange('coughQuality', e.target.value)}
            fullWidth
          />
          <TextField
            label='Cough Quantity'
            value={formState.coughQuantity}
            onChange={(e) => handleChange('coughQuantity', e.target.value)}
            fullWidth
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Nasal Flaring</InputLabel>
            <Select
              value={formState.nasalFlaring}
              label='Nasal Flaring'
              onChange={(e) => handleChange('nasalFlaring', e.target.value)}
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Shortness of Breath</InputLabel>
            <Select
              value={formState.breathShortness}
              label='Shortness of Breath'
              onChange={(e) => handleChange('breathShortness', e.target.value)}
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <FormControl fullWidth size="small" sx={{mb: 2}}>
          <InputLabel>ETT</InputLabel>
          <Select
            value={formState.ett}
            label='ETT'
            onChange={(e) => handleChange('ett', e.target.value)}
          >
            {yesNoOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label='ETT Size/Type'
          value={formState.ettSize}
          onChange={(e) => handleChange('ettSize', e.target.value)}
          fullWidth
          sx={{mb: 2}}
        />
        <FormControl fullWidth size="small" sx={{mb: 2}}>
          <InputLabel>O2</InputLabel>
          <Select
            value={formState.o2}
            label='O2'
            onChange={(e) => handleChange('o2', e.target.value)}
          >
            {yesNoOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label='O2 Amount'
            value={formState.o2Amount}
            onChange={(e) => handleChange('o2Amount', e.target.value)}
            fullWidth
          />
          <TextField
            label='Taken Via'
            value={formState.takenvia}
            onChange={(e) => handleChange('takenvia', e.target.value)}
            fullWidth
          />
        </Box>
        {buttons}
      </Box>
    </Box>
  )
}

export default NursesAssessmentForm3
